import { useNavigate } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useIdleLogout } from "@/hooks/useIdleLogout";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";

// Idle for IDLE_WARNING_AFTER_MINUTES with no activity anywhere in the
// app (any open tab — see useIdleLogout) shows this warning;
// IDLE_COUNTDOWN_SECONDS later with still no response, the session is
// force-logged-out. This is a financial app — an unattended, still
// logged-in tab is a real risk, so this defaults to a short, bank-grade
// window rather than a long/convenient one. Tune here if needed.
const IDLE_WARNING_AFTER_MINUTES = 4;
const IDLE_COUNTDOWN_SECONDS = 60;

export default function IdleLogoutDialog() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleTimeout = async () => {
    await logout();
    navigate("/login", { state: { reason: "idle" }, replace: true });
  };

  const { showWarning, secondsLeft, stayLoggedIn } = useIdleLogout({
    warningAfterMs: IDLE_WARNING_AFTER_MINUTES * 60 * 1000,
    countdownMs: IDLE_COUNTDOWN_SECONDS * 1000,
    onTimeout: handleTimeout,
    enabled: !!user,
  });

  if (!user) return null;

  return (
    <AlertDialog open={showWarning}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="w-10 h-10 rounded-full bg-danger/10 grid place-items-center mb-1">
            <ShieldAlert size={18} className="text-danger" strokeWidth={2.2} />
          </div>
          <AlertDialogTitle>Still there?</AlertDialogTitle>
          <AlertDialogDescription>
            For your security, you'll be signed out in {secondsLeft}s due to inactivity.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={stayLoggedIn}>
            Stay signed in
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
